import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { getKpiTypeColor } from "../../../utils/kpiColors";

const KPIRecurringDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { recurringDetail, taskTypes, eligibleUsers, loading } = useSelector((state) => state.kpi);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusError, setStatusError] = useState(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    task_name: "",
    task_description: "",
    task_kpi_type_id: "",
    task_priority: "Medium",
    frequency: "WEEKLY",
    assigned_to: [],
    project_id: ""
  });
  const [editError, setEditError] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch.kpi.getRecurringDetail(id);
      dispatch.kpi.getTaskTypes();
    }
  }, [dispatch, id]);

  const rawData = recurringDetail?.data || recurringDetail;
  const work = rawData?.work || rawData?.task || {};
  const kpiData = rawData?.kpi || work.kpi || {};
  const summary = rawData?.summary || {};
  const progress = rawData?.progress || {};
  const subtasks = rawData?.subtasks || [];
  const attachments = rawData?.attachments || [];
  const recConfig = rawData?.recurrence_configuration || {};

  useEffect(() => {
    if (work && Object.keys(work).length > 0) {
      const kpiInfo = kpiData || {};
      const kpiTypeId = work.kpi_type?._id || work.task_kpi_type_id || kpiInfo.kpi_type_id || "";
      const assignedUserIds = (work.assignees && work.assignees.length > 0)
        ? work.assignees.map(a => a._id || a)
        : (work.collaborators ? work.collaborators.map(c => c._id || c) : []);

      setEditForm({
        task_name: work.title || work.task_name || "",
        task_description: work.description || work.task_description || "",
        task_kpi_type_id: kpiTypeId,
        task_priority: work.priority || work.task_priority || "Medium",
        frequency: (recConfig.frequency || work.frequency || "WEEKLY").toUpperCase(),
        assigned_to: assignedUserIds,
        project_id: work.project?._id || work.project_id || ""
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurringDetail]);

  const recProjectId = work.project?._id || work.project_id || work.project?.id || (typeof work.project === 'string' ? work.project : null);

  useEffect(() => {
    if (recProjectId) {
      dispatch.kpi.getEligibleUsers({ project_id: recProjectId, hideLoading: true, limit: 100 });
    } else if (work && Object.keys(work).length > 0) {
      dispatch.kpi.setEligibleUsers([]);
    }
  }, [dispatch, recProjectId, work]);

  if (loading && !work._id && !work.title) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mb-4"></div>
          <p className="text-sm font-medium text-slate-500">Loading recurring schedule details...</p>
        </div>
      </div>
    );
  }

  if (!rawData || (!work._id && !work.title)) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center max-w-lg mx-auto my-12 shadow-xs">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4 text-slate-400">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
        </div>
        <h3 className="text-base font-bold text-slate-900">Recurring schedule not found</h3>
        <p className="text-xs text-slate-500 mt-1 mb-6">The schedule template you are looking for does not exist or may have been deleted.</p>
        <Link to="/kpi/recurring" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-slate-800 transition-colors !no-underline">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          <span>Back to Recurring Schedules</span>
        </Link>
      </div>
    );
  }

  const displayId = work.work_id || work.human_readable_id || (work._id ? `REC-${work._id.slice(-6)}` : id || "N/A");
  const title = work.title || work.task_name || "Untitled Work";
  const description = work.description || work.task_description || null;
  const priority = work.priority || work.task_priority || "Medium";
  const status = (recConfig.status || work.status || (work.is_active ? "ACTIVE" : "PAUSED")).toUpperCase();

  const typeObj = work.kpi_type || {};
  const typeName = typeObj.name || kpiData.type || "Task";
  const typeColor = getKpiTypeColor(typeName);

  const pointsVal = kpiData.assigned_points ?? typeObj.score ?? work.assigned_kpi_points ?? 0;
  const points = pointsVal !== undefined && pointsVal !== null ? pointsVal : 0;

  const frequency = recConfig.frequency || work.frequency || "WEEKLY";

  const nextExecutionDate = recConfig.next_generation_date || recConfig.next_execution || work.next_generation_date;
  const lastExecutionDate = recConfig.last_execution || work.last_generated_date;
  const daysUntilNext = work.days_until_next_generation;
  const occurrenceNumber = work.last_occurrence_number || 0;

  const createdDate = work.created_at || work.createdAt;
  const updatedDate = work.updated_at || work.updatedAt;

  const creatorUser = recConfig.created_by || work.created_by || work.owner || work.assigned_by;

  const assigneesList = (work.assignees && work.assignees.length > 0)
    ? work.assignees
    : (work.collaborators && work.collaborators.length > 0 ? work.collaborators : []);

  const projectName = (typeof work.project === 'object' ? (work.project?.name || work.project?.project_name || work.project?.title) : null) || work.project_name || work.projectName || (typeof work.project === 'string' ? work.project : null) || work.project_details?.name || "Standalone";

  const isCancelled = status === "CANCELLED";
  const isPaused = status === "PAUSED";
  const isActive = status === "ACTIVE";

  const isEditable = (recConfig.is_editable !== undefined ? Boolean(recConfig.is_editable) : (work.is_editable !== undefined ? Boolean(work.is_editable) : true)) && !isCancelled;
  const canEditStatus = recConfig.can_edit_status !== undefined ? Boolean(recConfig.can_edit_status) : (work.can_edit_status !== undefined ? Boolean(work.can_edit_status) : true);

  const isCancellable = canEditStatus && recConfig.is_cancellable !== false && !isCancelled;

  // Resolve is_resumable field strictly from API response data
  const isResumable = rawData?.is_resumable !== undefined 
    ? Boolean(rawData.is_resumable)
    : (recConfig.is_resumable !== undefined 
      ? Boolean(recConfig.is_resumable)
      : (work.is_resumable !== undefined ? Boolean(work.is_resumable) : false));

  const canPause = canEditStatus && isResumable && isActive && isEditable;
  const canResume = canEditStatus && isResumable && isPaused;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    } catch {
      return String(dateStr);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  const getUserInitials = (user) => {
    if (!user) return "?";
    if (typeof user === 'string') return user.substring(0, 2).toUpperCase();
    const first = user.first_name || user.full_name || user.name || "";
    const last = user.last_name || "";
    if (first || last) {
      if (user.full_name && !user.first_name) {
        const parts = user.full_name.trim().split(" ");
        return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
      }
      return ((first[0] || "") + (last[0] || "")).toUpperCase();
    }
    return "?";
  };

  const getUserFullName = (user) => {
    if (!user) return "N/A";
    if (typeof user === 'string') return user;
    if (user.full_name) return user.full_name;
    if (user.first_name || user.last_name) return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return user.name || user.email || "N/A";
  };

  const getAvatarBgColor = (initialsStr) => {
    if (!initialsStr) return "bg-[#147b56]";
    const charCode = (initialsStr.charCodeAt(0) || 0) + (initialsStr.charCodeAt(1) || 0);
    const colors = ["bg-[#ab6919]", "bg-[#1d5bbd]", "bg-[#147b56]", "bg-[#6558e8]", "bg-[#c73838]"];
    return colors[charCode % colors.length];
  };

  const updatedAtAgo = updatedDate ? formatTimeAgo(updatedDate) : null;
  const priorityLower = String(priority).toLowerCase();
  const isHighPriority = priorityLower === 'high';
  const isLowPriority = priorityLower === 'low';

  const handleStatusChange = async (newStatus) => {
    if (!canEditStatus) return;
    setStatusMessage(null);
    setStatusError(null);
    try {
      const res = await dispatch.kpi.updateRecurringStatus({ id: recConfig.recurrence_id || id, status: newStatus });
      if (res?.message) {
        setStatusMessage(res.message);
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "Failed to update schedule status";
      setStatusError(errMsg);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!isEditable) return;
    setEditError(null);

    if (!editForm.task_name.trim()) {
      setEditError("Task name is required");
      return;
    }

    try {
      const payload = {
        task_name: editForm.task_name,
        task_description: editForm.task_description,
        task_kpi_type_id: editForm.task_kpi_type_id,
        task_priority: editForm.task_priority,
        assigned_to: editForm.assigned_to
      };

      await dispatch.kpi.editRecurringSchedule({ id: recConfig.recurrence_id || id, payload });
      setIsEditModalOpen(false);
      setStatusMessage("Recurring schedule template updated successfully.");
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "Failed to update schedule template";
      setEditError(errMsg);
    }
  };

  const handleConfirmCancel = async () => {
    setIsCancelModalOpen(false);
    await handleStatusChange("CANCELLED");
  };

  return (
    <div className="w-full">
      {/* Top Header Bar & Action Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
          <Link to="/kpi/works" className="hover:text-slate-900 transition-colors">Works</Link>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          <Link to="/kpi/recurring" className="hover:text-slate-900 transition-colors">Recurring</Link>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          <span className="text-slate-900">{displayId}</span>
        </div>

        {/* Header Action Buttons */}
        <div className="flex items-center gap-3">
          {isEditable && (
            <button
              type="button"
              onClick={() => setIsEditModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-2xs cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              Edit Schedule
            </button>
          )}

          {canPause && (
            <button
              type="button"
              onClick={() => handleStatusChange("PAUSED")}
              className="flex items-center gap-2 px-4 py-2 border border-amber-200 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors shadow-2xs cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
              Pause Schedule
            </button>
          )}

          {canResume && (
            <button
              type="button"
              onClick={() => handleStatusChange("ACTIVE")}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors shadow-2xs cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
              Resume Schedule
            </button>
          )}

          {canEditStatus && isCancellable && (
            <button
              type="button"
              onClick={() => setIsCancelModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border border-rose-200 rounded-lg text-sm font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 transition-colors shadow-2xs cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              Cancel Schedule
            </button>
          )}
        </div>
      </div>

      {/* Notifications / Alerts */}
      {statusMessage && (
        <div className="mb-6 p-4 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center justify-between shadow-xs">
          <span>{statusMessage}</span>
          <button onClick={() => setStatusMessage(null)} className="text-xs text-emerald-600 hover:text-emerald-900 font-bold ml-4">&times;</button>
        </div>
      )}

      {statusError && (
        <div className="mb-6 p-4 rounded-xl text-sm font-medium bg-rose-50 text-rose-800 border border-rose-200 flex items-center justify-between shadow-xs">
          <span>{statusError}</span>
          <button onClick={() => setStatusError(null)} className="text-xs text-rose-600 hover:text-rose-900 font-bold ml-4">&times;</button>
        </div>
      )}

      {isCancelled && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs font-medium text-amber-900 flex items-start gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-600 shrink-0 mt-0.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
          <div>
            <span className="font-bold block text-amber-950">Cancelled Schedule Terminal State</span>
            <span>Cancelled recurring schedules cannot be resumed or modified. Please create a new recurring schedule if needed.</span>
          </div>
        </div>
      )}

      {/* Main Content Layout */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Main Content Card (Left Column) */}
        <div className="flex-1 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-6">
            {/* Title & Status Badges Header */}
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-500">{displayId}</span>
                {isActive && (
                  <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-emerald-50 text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    ACTIVE
                  </span>
                )}
                {isPaused && (
                  <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-amber-50 text-amber-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    PAUSED
                  </span>
                )}
                {isCancelled && (
                  <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-rose-50 text-rose-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                    CANCELLED
                  </span>
                )}
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${typeColor.badgeBg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${typeColor.bg}`}></span>
                  {typeName}
                </span>
                <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${isHighPriority ? 'bg-rose-50 text-rose-600' : isLowPriority ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isHighPriority ? 'bg-rose-500' : isLowPriority ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                  {priority}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{title}</h1>
              <p className="text-slate-500 text-sm">
                Created {createdDate ? formatDate(createdDate) : 'N/A'}{creatorUser ? ` by ${getUserFullName(creatorUser)}` : ''}{updatedAtAgo ? ` · last update ${updatedAtAgo}` : ''}
              </p>
            </div>

            {/* Quick Metrics Grid */}
            <div className="pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <span className="text-xs font-semibold text-slate-500 block">Project</span>
                <span className="text-sm font-bold text-slate-900 mt-0.5 block">{projectName}</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 block">Frequency</span>
                <span className="text-sm font-bold text-slate-900 mt-0.5 block">{frequency}</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 block">KPI Points</span>
                <span className="text-sm font-bold text-slate-900 mt-0.5 block">+{points} pts</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 block">Next Execution</span>
                <span className="text-sm font-bold text-slate-900 mt-0.5 block">{nextExecutionDate ? formatDate(nextExecutionDate) : "Pending"}</span>
              </div>
            </div>

            {/* Description Section */}
            <div className="pt-4 border-t border-slate-100">
              <h2 className="text-base font-semibold text-slate-900 mb-3">Description</h2>
              {description ? (
                <div className="text-sm text-slate-600 leading-relaxed bg-slate-50/70 p-4 rounded-xl border border-slate-100 whitespace-pre-line">
                  {description}
                </div>
              ) : (
                <div className="text-sm text-slate-400 italic">No description provided.</div>
              )}
            </div>

            {/* People & Assignees Section */}
            <div className="pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Creator / Owner */}
              <div>
                <span className="text-xs font-semibold text-slate-500 block mb-2">Schedule Owner</span>
                {creatorUser ? (
                  <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {getUserInitials(creatorUser)}
                    </div>
                    <div className="truncate">
                      <div className="text-xs font-bold text-slate-900 truncate">{getUserFullName(creatorUser)}</div>
                      {creatorUser.email && <div className="text-[11px] text-slate-500 truncate">{creatorUser.email}</div>}
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-slate-400 italic">N/A</span>
                )}
              </div>

              {/* Target Assignees */}
              <div>
                <span className="text-xs font-semibold text-slate-500 block mb-2">Target Assignees</span>
                {assigneesList.length > 0 ? (
                  <div className="space-y-2">
                    {assigneesList.map((user, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <div className={`w-8 h-8 rounded-full ${getAvatarBgColor(getUserInitials(user))} text-white flex items-center justify-center text-xs font-bold shrink-0`}>
                          {getUserInitials(user)}
                        </div>
                        <div className="truncate">
                          <div className="text-xs font-bold text-slate-900 truncate">{getUserFullName(user)}</div>
                          {user.email && <div className="text-[11px] text-slate-500 truncate">{user.email}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-slate-400 italic">No assigned collaborators</span>
                )}
              </div>
            </div>
          </div>

          {/* Subtasks (if present) */}
          {subtasks.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-slate-900">Subtasks</h2>
                <span className="text-xs text-slate-500 font-medium">{summary.completed || 0}/{summary.total_subtasks || subtasks.length} completed</span>
              </div>
              <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mb-4">
                <div className="bg-emerald-600 h-full transition-all" style={{ width: `${progress.percentage || 0}%` }}></div>
              </div>
              <div className="divide-y divide-slate-100">
                {subtasks.map((st, sIdx) => (
                  <div key={st._id || sIdx} className="py-2.5 flex items-center justify-between text-xs text-slate-700 font-medium">
                    <span>{st.title || st.task_name}</span>
                    <span className="text-slate-400 text-[10px] font-bold">{st.status || "Pending"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}


        </div>

        {/* Sidebar (Right Column) */}
        <div className="w-full lg:w-80 shrink-0 space-y-6">
          <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs p-6">
            <h3 className="text-sm font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100">Schedule Overview</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Schedule ID</span>
                <span className="text-xs font-mono font-bold text-slate-800">{displayId}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Schedule Status</span>
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${isActive ? 'bg-emerald-50 text-emerald-700' : isPaused ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'}`}>
                  {status}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Frequency</span>
                <span className="text-xs font-bold text-slate-900">{frequency}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">KPI Points</span>
                <span className="bg-black text-white text-xs font-bold px-3 py-1 rounded-full shadow-2xs">
                  +{points}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Next Generation</span>
                <span className="text-xs font-bold text-slate-900">
                  {nextExecutionDate ? formatDate(nextExecutionDate) : "Pending"}
                </span>
              </div>
              {daysUntilNext !== undefined && daysUntilNext !== null && (
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-slate-500">Days Remaining</span>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                    {daysUntilNext} day(s)
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Occurrences</span>
                <span className="text-xs font-bold text-slate-900">{occurrenceNumber} generated</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Last Execution</span>
                <span className="text-xs font-bold text-slate-900">
                  {lastExecutionDate ? formatDate(lastExecutionDate) : "None yet"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Created</span>
                <span className="text-xs font-bold text-slate-900">{formatDate(createdDate)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Schedule Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900">Edit Schedule Template</h3>
              <button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-slate-700 font-bold text-lg">&times;</button>
            </div>

            {editError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg">
                {editError}
              </div>
            )}

            <form onSubmit={handleEditSubmit} className="space-y-4 text-xs">
              <div className="space-y-1">
                <label className="font-semibold text-slate-700 block">Task Name <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  required
                  value={editForm.task_name}
                  onChange={(e) => setEditForm({ ...editForm, task_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 text-slate-900"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-slate-700 block">Description</label>
                <textarea
                  rows="3"
                  value={editForm.task_description}
                  onChange={(e) => setEditForm({ ...editForm, task_description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 text-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 block">Work Type</label>
                  <Select
                    isDisabled={true}
                    options={(taskTypes || []).map((t) => ({ value: t._id, label: `${t.name} (${t.score} pts)` }))}
                    className="text-sm text-slate-900"
                    classNamePrefix="select"
                    placeholder="Select type..."
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    onChange={(opt) => setEditForm({ ...editForm, task_kpi_type_id: opt ? opt.value : "" })}
                    value={(taskTypes || []).filter((t) => t._id === editForm.task_kpi_type_id).map((t) => ({ value: t._id, label: `${t.name} (${t.score} pts)` }))[0] || null}
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 block">Priority</label>
                  <Select
                    options={[
                      { value: "Low", label: "Low" },
                      { value: "Medium", label: "Medium" },
                      { value: "High", label: "High" }
                    ]}
                    className="text-sm text-slate-900"
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    onChange={(opt) => setEditForm({ ...editForm, task_priority: opt ? opt.value : "Medium" })}
                    value={{ value: editForm.task_priority, label: editForm.task_priority }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 block">Project / Team</label>
                  <input
                    type="text"
                    readOnly
                    disabled
                    value={projectName || "Standalone"}
                    className="w-full px-3 py-2 border border-slate-200 bg-slate-100/80 rounded-lg text-sm font-medium text-slate-600 cursor-not-allowed select-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 block">Frequency</label>
                  <input
                    type="text"
                    readOnly
                    disabled
                    value={frequency || "WEEKLY"}
                    className="w-full px-3 py-2 border border-slate-200 bg-slate-100/80 rounded-lg text-sm font-medium text-slate-600 cursor-not-allowed select-none"
                  />
                </div>
              </div>

              {/* Single Assignee Selection Constraint */}
              <div className="space-y-1">
                <label className="font-semibold text-slate-700 block">Assignee (Single owner)</label>
                <Select
                  options={(eligibleUsers || []).map((u) => ({ value: u._id || u.id, label: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email }))}
                  className="text-sm text-slate-900"
                  classNamePrefix="select"
                  placeholder={recProjectId ? "Select single assignee..." : "No project associated"}
                  isDisabled={!recProjectId}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  onChange={(opt) => setEditForm({ ...editForm, assigned_to: opt ? [opt.value] : [] })}
                  value={(eligibleUsers || []).filter((u) => editForm.assigned_to.includes(u._id || u.id)).map((u) => ({ value: u._id || u.id, label: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email }))[0] || null}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border border-slate-200 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors shadow-xs"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {isCancelModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Cancel Recurring Schedule</h3>
                <p className="text-xs text-rose-600 font-semibold">Terminal State Warning</p>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to cancel <span className="font-semibold text-slate-900">"{title}"</span>?
              <br/><br/>
              <span className="text-rose-600 font-bold block">Cancelled recurring schedules cannot be resumed or modified.</span>
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsCancelModalOpen(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors"
              >
                Keep Active
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="px-4 py-2 bg-rose-600 text-white rounded-xl text-xs font-semibold hover:bg-rose-700 transition-colors shadow-xs"
              >
                Confirm Cancel Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIRecurringDetails;
